import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useLocation } from 'react-router-dom';

import useUserStore from 'hooks/useUserStore';
import { useTranslation, Trans } from 'react-i18next';
import { ButtonCloud } from '@experimental-components/ButtonCloud';
import { ButtonDownload } from '@experimental-components/ButtonDownload';
import { useDownloadTemplate, useImportMenuModal } from 'components/MenuMakeImport/hooks';
import { useNotification } from '@design-system/Notification/useNotification';
import { MENU_ROUTES } from 'utils/menuMaker/constants';

export const EmptyMenus = () => {
  const {
    storeState: { selected },
  } = useUserStore();

  const isDraftStore = selected?.isDraft;

  return isDraftStore ? <EmptyDraft /> : <EmptyNoDraft />;
};

const EmptyDraft = () => {
  const { mutate: downloadTemplate } = useDownloadTemplate();
  const { handleOpen } = useImportMenuModal();
  const { t } = useTranslation();
  const classes = useStyles();
  const { setNotification } = useNotification();
  const location = useLocation();

  /**
   * Determines if download/import buttons should be shown based on current route
   * Excludes articles and modifier groups pages
   *
   * @returns {boolean} Whether to show the buttons section
   */
  const shouldShowDownloadImportButtons = () => {
    const currentPath = location.pathname;
    const excludedPaths = [MENU_ROUTES.PRODUCTS, MENU_ROUTES.MODIFIER_GROUPS];
    return !excludedPaths.includes(currentPath);
  };

  /**
   * Gets the appropriate description text based on current route
   * Shows different text for articles and modifier groups pages
   *
   * @returns {string} Translation key for the description text
   */
  const getDescriptionText = () => {
    const currentPath = location.pathname;
    if (currentPath === MENU_ROUTES.PRODUCTS || currentPath === MENU_ROUTES.MODIFIER_GROUPS) {
      return <Trans i18nKey="menuMaker:emptyMenus.draft.descriptionWithoutProductsAndModifiersGroups" />;
    }
    return <Trans i18nKey="menuMaker:emptyMenus.draft.description2" />;
  };

  const handleDownloadTemplate = (type) => {
    downloadTemplate(
      { type },
      {
        onSuccess: (res) => {
          const url = window.URL.createObjectURL(res);
          const a = document.createElement('a');
          a.href = url;
          a.download =
            type === 'EMPTY'
              ? `${t('menuMaker:emptyMenus.draft.emptyTemplateFileName')}.xlsx`
              : `${t('menuMaker:emptyMenus.draft.exampleTemplateFileName')}.xlsx`;
          a.click();
          window.URL.revokeObjectURL(url);
        },
        onError: (error) => {
          setNotification(error.message, 'error');
        },
      },
    );
  };
  return (
    <Box className={classes.root} data-testid="resumeMenusNoData">
      <IconEmpty />
      <Typography className={classes.title} variant="h6">
        {t('menuMaker:emptyMenus.draft.title')}
      </Typography>
      <Typography className={classes.description} variant="body1">
        {t('menuMaker:emptyMenus.draft.description')}
      </Typography>
      <Typography className={classes.description} variant="body1">
        {getDescriptionText()}
      </Typography>
      {shouldShowDownloadImportButtons() && (
        <>
          <Box display="flex" flexDirection="column" gap={2}>
            <ButtonDownload fullWidth onClick={() => handleDownloadTemplate('EMPTY')} size="large" variant="outlined">
              {t('menuMaker:emptyMenus.draft.downloadButton')}
            </ButtonDownload>
            <ButtonCloud fullWidth onClick={handleOpen} size="large" variant="black">
              {t('menuMaker:emptyMenus.draft.importButton')}
            </ButtonCloud>
          </Box>
          <Typography className={classes.example} onClick={() => handleDownloadTemplate('EXAMPLE')}>
            {t('menuMaker:emptyMenus.draft.example')}
          </Typography>
        </>
      )}
    </Box>
  );
};

const EmptyNoDraft = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  return (
    <Box className={classes.root} data-testid="resumeMenusNoData">
      <IconEmpty />
      <Typography className={classes.title} variant="h6">
        <Trans components={{ br: <br /> }} i18nKey="menuMaker:emptyMenus.noDraft.title" />
      </Typography>
      <Typography className={classes.description} variant="body1">
        <Trans i18nKey="menuMaker:emptyMenus.noDraft.description" />
      </Typography>
      <Typography className={classes.description} variant="body1">
        <Trans components={{ strong: <strong />, br: <br /> }} i18nKey="menuMaker:emptyMenus.noDraft.description2" />
      </Typography>
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(15),
    maxWidth: theme.spacing(63),
    marginLeft: 'auto',
    marginRight: 'auto',
    textAlign: 'center',
  },
  example: {
    fontSize: 14,
    textDecoration: 'underline',
    color: '#F04E4A',
    cursor: 'pointer',
  },
  title: {
    fontSize: theme.typography.h4.fontSize,
    lineHeight: theme.typography.h4.lineHeight,
  },
  description: {
    fontSize: 16,
    color: theme.palette.text.secondary,
    '& strong': {
      fontWeight: 500,
    },
  },
}));

const IconEmpty = () => {
  return (
    <svg fill="none" height="90" viewBox="0 0 98 90" width="98" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip0_164_7940)">
        <path d="M12.4473 89.7843H76.5646L87.3835 42.0781H23.2662L12.4473 89.7843Z" fill="#CECDD1" />
        <path d="M31 13.7363L79.6716 12.0928L93 72.3334L43.4781 75.0928L31 13.7363Z" fill="#A3A3AF" />
        <path d="M32 12.7301L80.6716 11.0928L94 70.3971L44.4781 73.0928L32 12.7301Z" fill="#FFE8E8" />
        <path
          d="M32.1373 12.759V12.8601L80.5018 11.2022L93.7255 71.0481L44.6517 73.7776L32.2517 12.7388L32.1373 12.759L32.0229 12.7792L44.4686 74L94 71.2301L80.6849 11L32 12.6579L32.0229 12.7792L32.1373 12.759Z"
          fill="#3D3D7E"
        />
        <path d="M40.7422 22.7016L42.5316 32.0312L53.7319 31.4123L51.7437 22.289L40.7422 22.7016Z" fill="#F04E4A" />
        <path d="M43.3398 37.6391L45.2187 46.9688L56.979 46.3498L54.8914 37.2265L43.3398 37.6391Z" fill="#F04E4A" />
        <path
          d="M43.3398 27.1319L47.7901 30.082L51.1337 20.5456L50.4508 20.3398L47.4133 29.0072L43.7401 26.583L43.3398 27.1319Z"
          fill="white"
        />
        <path
          d="M46.5879 42.0773L50.6673 45.0205L53.7322 35.5064L53.1063 35.2782L50.3219 43.9482L46.9548 41.5297L46.5879 42.0773Z"
          fill="white"
        />
        <path d="M55.7251 25.5361L76.4632 24.2754L76.4178 23.5877L55.6797 24.8484" fill="#EF3543" />
        <path d="M55.7246 28.7832L76.4632 27.4633L76.4183 26.8347L55.6797 28.1756" fill="#EF3543" />
        <path d="M59.6235 41.124L80.3616 39.8714L80.3162 39.1756L59.5781 40.4281" fill="#EF3543" />
        <path d="M59.6245 44.3711L81.0111 43.0512L80.9648 42.4226L59.5781 43.7426" fill="#EF3543" />
        <path d="M51.1792 68.5977L74.5162 66.7383L74.4718 65.9997L51.1348 67.8336" fill="#EF3543" />
        <path
          d="M65.2433 46.5925H13.1891L12.6157 43.8914C12.5161 43.4236 12.0466 43.083 11.497 43.083H0.273438L1.12597 46.5925L11.9449 89.7867H76.0622L65.2433 46.5925Z"
          fill="white"
        />
        <path
          d="M76.3405 90.0009H11.7775L0.917445 46.645L0 42.8682H11.4984C12.1497 42.8682 12.7079 43.2799 12.8291 43.8469L13.3657 46.3777H65.4134L76.3405 90.0009ZM12.115 89.5697H75.7866L65.0759 46.8067H13.0152L12.4071 43.9352C12.3293 43.5666 11.9463 43.2972 11.5005 43.2972H0.549601L1.33722 46.5394L12.115 89.5697Z"
          fill="black"
        />
        <path
          d="M37.8714 61.1023L35.4912 58.3516L33.1089 61.1023L27.0352 68.1213L30.1294 70.0485L33.572 66.0712V77.9256H37.4105V66.0712L40.8531 70.0485L43.9451 68.1213L37.8714 61.1023Z"
          fill="#F04E4A"
        />
        <path
          d="M9.14839 22.9674L9.91097 26.2289C9.91097 26.2289 10.0231 27.1305 11.4947 27.3427C11.4947 27.3427 27.4872 30.4865 36.6264 23.0233C36.6264 23.0233 37.1461 22.6116 36.9596 21.1534L36.6363 18L9.15234 21.3608C9.15234 21.3608 8.81222 21.5305 9.14839 22.9674Z"
          fill="white"
        />
        <path
          d="M9.30386 22.4743L9.45329 22.4403L10.2358 25.9283L10.2368 25.9365L10.1423 25.9478L10.2368 25.9355V25.9365L10.1423 25.9478L10.2368 25.9355V25.9365C10.2398 25.9581 10.2775 26.1701 10.4777 26.4006C10.6779 26.6302 11.0407 26.8864 11.7307 26.9914L11.7358 26.9924H11.7389C11.7389 26.9924 11.7511 26.9955 11.7775 27.0007C12.1647 27.0758 15.4382 27.6912 19.7503 27.6912C22.4801 27.6912 25.6266 27.4443 28.7181 26.661C31.8107 25.8778 34.8475 24.5584 37.3658 22.4156L37.3678 22.4146L37.4278 22.4887L37.3658 22.4156L37.3678 22.4146L37.4278 22.4887L37.3658 22.4156L37.3719 22.4239L37.3658 22.4166V22.4156L37.3719 22.4239L37.3658 22.4166C37.3669 22.4156 37.4471 22.3435 37.5294 22.1531C37.6107 21.9638 37.6951 21.653 37.6951 21.1723C37.6951 20.9922 37.6829 20.7884 37.6545 20.5579V20.5548L37.3384 17.3458L9.32625 20.913L9.30791 20.7596L9.37706 20.8965L9.32927 20.8029L9.39231 20.8883L9.37706 20.8965L9.32927 20.8029L9.39231 20.8883L9.36785 20.8543L9.39429 20.8863L9.39231 20.8883L9.36785 20.8543L9.39429 20.8863C9.39226 20.8883 9.37092 20.9099 9.34856 20.9809C9.3262 21.0509 9.30489 21.1682 9.30489 21.3535C9.30489 21.5912 9.34048 21.9402 9.45329 22.4403L9.30386 22.4743L9.15546 22.5082C9.03961 21.9926 9 21.6221 9 21.3535C9 21.0808 9.04065 20.911 9.09147 20.7988C9.14228 20.6866 9.20833 20.6372 9.2378 20.6217L9.26217 20.6093L37.6117 17L37.9573 20.5239L37.8059 20.5383L37.9573 20.5198C37.9868 20.7617 38 20.9789 38 21.1723C38 21.7147 37.9004 22.078 37.7936 22.3106C37.6879 22.5422 37.5762 22.641 37.5599 22.6544L37.4644 22.5339L37.562 22.6523C34.9978 24.8332 31.9165 26.1691 28.7924 26.9605C25.6673 27.752 22.4984 28 19.7503 28C15.1201 28 11.6851 27.2971 11.679 27.295L11.7084 27.1437L11.686 27.2971C10.9025 27.1798 10.4543 26.8617 10.2134 26.5622C9.97157 26.2627 9.936 25.9921 9.93396 25.9735L10.0854 25.955L9.93698 25.989L9.15546 22.5082L9.30386 22.4743Z"
          fill="#211D1C"
        />
        <path
          d="M9.80482 19.0258C9.80482 19.0258 7.4403 20.1763 8.12413 22.1628C8.12413 22.1628 8.50776 22.9002 9.79793 22.1657C9.79793 22.1657 10.2149 21.5437 11.5482 22.7937C12.545 23.7282 13.5203 24.4299 14.831 23.6914C15.7798 23.156 16.8463 22.6226 17.9569 22.526C19.248 22.4126 20.0869 23.2585 21.2868 23.4456C22.3336 23.6088 23.2264 22.8545 23.8239 22.0255C24.183 21.5288 24.8668 21.1805 25.4114 21.0004C26.0196 20.7994 26.6623 20.7754 27.2863 20.9018C28.0574 21.058 28.808 21.5716 29.6115 21.3776C30.3532 21.1984 30.937 20.5744 31.354 19.9385C31.354 19.9385 31.9917 18.5282 34.265 19.3264C34.265 19.3264 35.7454 20.2331 36.753 18.6517C36.753 18.6517 41.6223 17.6415 37.0248 14.9674L35.5983 14L9.80482 19.0258Z"
          fill="#CECDD1"
        />
        <path
          d="M8.41852 20.131C8.19845 20.0834 8.03859 19.8989 8.02478 19.6811C7.90043 17.5871 7.83531 7.20334 18.6 5.19676C18.6 5.19676 32.6242 2.73747 36.9732 15.2077C37.0492 15.4274 36.9594 15.668 36.7541 15.786C34.6186 17.0165 22.8197 23.2626 8.41852 20.131Z"
          fill="#F04E4A"
        />
        <path
          d="M37.8848 20.0279C37.8134 19.9869 37.7267 19.9911 37.6594 20.0402C37.6308 20.0618 34.7447 22.1525 29.822 23.7997C25.2807 25.3178 18.0277 26.759 9.25822 24.9992C9.18479 24.9838 9.10724 25.0125 9.05625 25.074C9.00526 25.1354 8.98797 25.2215 9.00836 25.3014L10.1465 29.5914C10.1669 29.6693 10.2219 29.7297 10.2923 29.7533C10.328 29.7656 13.8811 30.9395 19.0292 30.9979C19.1343 30.9999 19.2392 30.9999 19.3463 30.9999C24.0936 31.0102 31.0621 30.0401 37.8838 25.4991C37.949 25.4561 37.9888 25.3792 37.9898 25.2952L38 20.24C38 20.1519 37.9562 20.0699 37.8848 20.0279Z"
          fill="#F04E4A"
        />
        <path
          d="M14.0187 10.2092C13.8643 10.7301 14.7015 11.3665 15.1583 11.7001C17.08 13.1044 15.282 9.05695 14.0187 10.2092Z"
          fill="#F0F0F0"
        />
        <path
          d="M20.5171 7C20.088 7.21399 20.0279 8.01887 20.0037 8.44949C19.904 10.2644 21.8842 7.01145 20.5171 7Z"
          fill="#F0F0F0"
        />
        <path
          d="M26.0005 8.30911C25.9838 8.89913 26.4031 9.46825 26.6305 9.76326C27.5884 11.0083 26.4309 6.86707 26.0005 8.30911Z"
          fill="#F0F0F0"
        />
        <path
          d="M19.241 14C18.4418 14.0853 18.1759 14.4917 18.0486 14.7096C17.5121 15.6282 21.6482 14.0812 19.241 14Z"
          fill="#F0F0F0"
        />
        <path
          d="M24.268 12.0856C23.639 12.6402 24.268 13.7586 24.6217 14.3524C26.1149 16.852 26.9102 11.2511 24.268 12.0856Z"
          fill="#F0F0F0"
        />
      </g>
      <defs>
        <clipPath id="clip0_164_7940">
          <rect fill="white" height="90" width="98" />
        </clipPath>
      </defs>
    </svg>
  );
};
